import { useId, useMemo, useState } from "react";
import type { PlatformLocationModel1 } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";
import TextInput from "@/form/inputs/TextInput/TextInput";
import { Button, Checkbox, Popover, ScrollableFilterTags, type Tag } from "@/newComponents";
import { ReactComponent as FilterIcon } from "atoms/icons/filter.svg";
import { ReactComponent as SearchIcon } from "atoms/icons/search.svg";

export type LocationFilterItem = { locationIds: string[] };
export const filterByLocation = <T extends LocationFilterItem>(
  items: readonly T[],
  selectedLocationIds: readonly string[],
): T[] => {
  if (selectedLocationIds.length === 0) return [...items];
  return items.filter(
    ({ locationIds }) =>
      locationIds.length === 0 || locationIds.some((id) => selectedLocationIds.includes(id)),
  );
};
type LocationFilterProps = {
  locations: PlatformLocationModel1[];
  selectedLocationIds: string[];
  disabled?: boolean;
  dataTestId?: string;
  onChange: (locationIds: string[]) => void;
};
const locationFilterTags = (
  locations: PlatformLocationModel1[],
  selectedLocationIds: readonly string[],
): Tag[] =>
  locations
    .filter(({ id }) => selectedLocationIds.includes(id))
    .map(({ id, name }) => ({ id, label: name }));

export const LocationFilter = ({
  locations,
  selectedLocationIds,
  disabled,
  dataTestId,
  onChange,
}: LocationFilterProps) => {
  const { t } = useTranslation();
  const searchId = useId();
  const [query, setQuery] = useState("");
  const filteredLocations = useMemo(
    () => locations.filter(({ name }) => name.toLowerCase().includes(query.toLowerCase())),
    [locations, query],
  );
  const toggleLocation = (id: string, checked: boolean) =>
    onChange(
      checked ? [...selectedLocationIds, id] : selectedLocationIds.filter((value) => value !== id),
    );
  return (
    <div data-testid={dataTestId}>
      <Popover
        avoidCollisions
        disabled={disabled}
        content={
          <div className="w-72 p-3">
            <label className="sr-only" htmlFor={searchId}>
              {t("common.search")}
            </label>
            <TextInput
              aria-describedby={`${searchId}-description`}
              disabled={disabled}
              id={searchId}
              name="location-filter-search"
              placeholder={t("common.search")}
              value={query}
              leftIcon={<SearchIcon className="h-4 w-4" />}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <span className="sr-only" id={`${searchId}-description`}>
              {t("common.locationsPicker.selectLocations")}
            </span>
            <div
              className="mt-2 max-h-64 overflow-y-auto"
              role="group"
              aria-label={t("common.locationsPicker.selectLocations")}
            >
              {filteredLocations.map(({ id, name }) => (
                <Checkbox
                  key={id}
                  checked={selectedLocationIds.includes(id)}
                  disabled={disabled}
                  className="type-body-sm"
                  onCheckedChange={(checked) => toggleLocation(id, checked)}
                >
                  {name}
                </Checkbox>
              ))}
            </div>
            <Button
              disabled={disabled || selectedLocationIds.length === 0}
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => onChange([])}
            >
              {t("common.clear")}
            </Button>
          </div>
        }
        side="bottom"
      >
        <Button disabled={disabled} StartIcon={FilterIcon} variant="secondary">
          {t("common.filters")}
        </Button>
      </Popover>
    </div>
  );
};

type LocationFilterTagsProps = {
  locations: PlatformLocationModel1[];
  selectedLocationIds: string[];
  disabled?: boolean;
  dataTestId?: string;
  onChange: (locationIds: string[]) => void;
};
export const LocationFilterTags = ({
  locations,
  selectedLocationIds,
  disabled,
  dataTestId,
  onChange,
}: LocationFilterTagsProps) => {
  const { t } = useTranslation();
  const tags = locationFilterTags(locations, selectedLocationIds);
  return (
    <div data-testid={dataTestId}>
      {tags.length > 0 && (
        <ScrollableFilterTags
          ariaLabel={t("common.locationsPicker.selectedLocations")}
          scrollLeftLabel={t("common.locationsPicker.scrollLeft")}
          scrollRightLabel={t("common.locationsPicker.scrollRight")}
          disabled={disabled}
          tags={tags}
          onTagRemove={(tag) => onChange(selectedLocationIds.filter((id) => id !== tag.id))}
        />
      )}
    </div>
  );
};
